/**
 * CriteriaListPanel — the left pane of the Inato-style split-view results page.
 *
 * Top: the ReviewQueuePanel (dense inline-action queue of every criterion
 * still awaiting a human decision — the no-scroll fast path), then filter
 * pill tabs [All] [Needs attention] [Eligible] + the summary chip row
 * "I/E CRITERIA MET: ✓N ✗N ?N" (counts use the EFFECTIVE verdict — the
 * human override when verified, else the AI result — mapped to eligibility
 * impact). Then the "Inclusion Criteria" and "Exclusion Criteria" sections,
 * each with a small disclaimer line, rows sorted blocking (NOT ELIGIBLE)
 * first, then NEEDS REVIEW, then ELIGIBLE — stable by criterion_number inside
 * each band. "Needs attention" = blocking + needs-review + any row flagged
 * requires_human_review; "Eligible" = eligible-impact rows (which since
 * 2026-07-11 includes not_found EXCLUSIONS — no evidence of an exclusion
 * doesn't block and needs no confirmation; see impactOf).
 */
import { useMemo, useState } from 'react';
import { Check, HelpCircle, X } from 'lucide-react';
import { EligibilityCitation, EligibilityCriterionResult } from '../../types';
import CriterionRow, { EligibilityImpact, effectiveResult, impactOf } from './CriterionRow';
import ReviewQueuePanel from './ReviewQueuePanel';

type FilterTab = 'all' | 'attention' | 'eligible';

const SECTION_DISCLAIMER = 'AI pre-screening aid — it can make mistakes. Verify against the sources.';

const BAND_RANK: Record<EligibilityImpact, number> = {
  not_eligible: 0,
  needs_review: 1,
  eligible: 2,
};

const effectiveImpact = (r: EligibilityCriterionResult): EligibilityImpact =>
  impactOf(effectiveResult(r), r.criterion_kind).impact;

const needsAttention = (r: EligibilityCriterionResult): boolean =>
  effectiveImpact(r) !== 'eligible' || r.requires_human_review;

const byBandThenNumber = (a: EligibilityCriterionResult, b: EligibilityCriterionResult): number => {
  const band = BAND_RANK[effectiveImpact(a)] - BAND_RANK[effectiveImpact(b)];
  if (band !== 0) return band;
  return (a.criterion_number || '').localeCompare(b.criterion_number || '', undefined, { numeric: true });
};

interface Props {
  results: EligibilityCriterionResult[];
  /** document_id → filename, for citation chip labels. */
  documentNames: Record<string, string>;
  onCitationClick: (citation: EligibilityCitation) => void;
  /** One-click verify-agree. Omit both actions after sign-off. */
  onConfirm?: (r: EligibilityCriterionResult) => Promise<void>;
  onEdit?: (r: EligibilityCriterionResult) => void;
  /** §3.5: the criteria derive from a superseded eReg document version. */
  sourceStale?: boolean;
}

function Section({
  title,
  rows,
  documentNames,
  onCitationClick,
  onConfirm,
  onEdit,
}: {
  title: string;
  rows: EligibilityCriterionResult[];
} & Omit<Props, 'results'>) {
  if (rows.length === 0) return null;
  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="border-b border-gray-100 px-3 py-2 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {title} <span className="text-xs font-normal text-gray-400">({rows.length})</span>
        </h2>
        <p className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">{SECTION_DISCLAIMER}</p>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
        {rows.map((r) => (
          <CriterionRow
            key={r.id}
            result={r}
            documentNames={documentNames}
            onCitationClick={onCitationClick}
            onConfirm={onConfirm}
            onEdit={onEdit}
          />
        ))}
      </div>
    </section>
  );
}

export default function CriteriaListPanel({
  results,
  documentNames,
  onCitationClick,
  onConfirm,
  onEdit,
  sourceStale = false,
}: Props) {
  const [filter, setFilter] = useState<FilterTab>('all');

  // Summary counts across ALL criteria (never filtered), by effective impact.
  const summary = useMemo(() => {
    const acc = { eligible: 0, not_eligible: 0, needs_review: 0 };
    for (const r of results) acc[effectiveImpact(r)] += 1;
    return acc;
  }, [results]);

  const attentionCount = useMemo(() => results.filter(needsAttention).length, [results]);

  const tabs: { key: FilterTab; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: results.length },
    { key: 'attention', label: 'Needs attention', count: attentionCount },
    { key: 'eligible', label: 'Eligible', count: summary.eligible },
  ];

  const filtered = useMemo(() => {
    if (filter === 'attention') return results.filter(needsAttention);
    if (filter === 'eligible') return results.filter((r) => effectiveImpact(r) === 'eligible');
    return results;
  }, [results, filter]);

  const inclusions = useMemo(
    () => filtered.filter((r) => r.criterion_kind !== 'exclusion').sort(byBandThenNumber),
    [filtered]);
  const exclusions = useMemo(
    () => filtered.filter((r) => r.criterion_kind === 'exclusion').sort(byBandThenNumber),
    [filtered]);

  return (
    <div className="space-y-4">
      {/* Fast-path queue: every criterion awaiting a human decision, with
          inline Confirm/Edit — consecutive clicks, no scroll-hunting. */}
      <ReviewQueuePanel
        results={results}
        documentNames={documentNames}
        onCitationClick={onCitationClick}
        onConfirm={onConfirm}
        onEdit={onEdit}
        notice={sourceStale
          ? 'The study protocol was re-versioned in eReg after these criteria were extracted — consider re-extracting the criteria on the study page before relying on this review.'
          : null}
      />

      {/* Filter pills + summary chips */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex flex-wrap items-center gap-1.5" role="tablist" aria-label="Filter criteria">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={filter === t.key}
              onClick={() => setFilter(t.key)}
              className={`inline-flex min-h-[44px] items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 ${
                filter === t.key
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {t.label}
              <span className={filter === t.key ? 'opacity-80' : 'text-gray-400 dark:text-gray-400'}>{t.count}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            I/E criteria met:
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-300">
            <Check size={12} />
            {summary.eligible}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-300">
            <X size={12} />
            {summary.not_eligible}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            <HelpCircle size={12} />
            {summary.needs_review}
          </span>
        </div>
      </div>

      <Section
        title="Inclusion Criteria"
        rows={inclusions}
        documentNames={documentNames}
        onCitationClick={onCitationClick}
        onConfirm={onConfirm}
        onEdit={onEdit}
      />
      <Section
        title="Exclusion Criteria"
        rows={exclusions}
        documentNames={documentNames}
        onCitationClick={onCitationClick}
        onConfirm={onConfirm}
        onEdit={onEdit}
      />

      {filtered.length === 0 && (
        <p className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-500 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
          {results.length === 0
            ? 'No criterion results were recorded for this review.'
            : 'No criteria match this filter.'}
        </p>
      )}
    </div>
  );
}
